import { initializeApp } from 'firebase/app'
import { ensureInitialized, fetchAndActivate, getValue, getAll, getRemoteConfig } from 'firebase/remote-config'
import { until } from '../helpers'

const minutesBetweenReload = 5
const minutesMillis = 60 * 1000

export class RemoteConfigService {
  static intervalMillis = minutesBetweenReload * minutesMillis // 3 minutes

  constructor(
    app = initializeApp(window.firebaseConfig),
    private remoteConfig = getRemoteConfig(app),
    private _initialized = false,
    private _error = false
  ) {
    remoteConfig.settings.minimumFetchIntervalMillis = RemoteConfigService.intervalMillis
    this.autoload.call(this)
  }

  get initialized() {
    return this._initialized
  }

  get error() {
    return this._error
  }

  private dispatchEvent() {
    const event = new Event('remote-config-reload')
    document.dispatchEvent(event)
  }

  public addEventListener(listener) {
    document.addEventListener('remote-config-reload', listener)
  }

  public removeEventListener(listener) {
    document.removeEventListener('remote-config-reload', listener)
  }

  private async autoload() {
    try {
      const result = await this.fetchAndActivate()
      if (result) {
        this.dispatchEvent()
      }
      setTimeout(this.autoload.bind(this), RemoteConfigService.intervalMillis)
      this._error = false
    } catch {
      this._error = true
    } finally {
      this._initialized = true
    }
  }

  public get lastFetchStatus() {
    return this.remoteConfig.lastFetchStatus
  }

  public async ensureInitialized() {
    await ensureInitialized(this.remoteConfig)
    await until(() => this._initialized)
  }

  public fetchAndActivate() {
    return fetchAndActivate(this.remoteConfig)
  }

  public getValue(key: string) {
    return getValue(this.remoteConfig, key)
  }

  public getAll() {
    return getAll(this.remoteConfig)
  }
}

export default new RemoteConfigService()
